import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import api from "Common/api";
import { AlertCircle, Check, CreditCard, Loader2, Plus, ShieldCheck } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { stripePromise, stripePromiseTest } from "slices/apiEndpoint";
import "./billing-recovery-modal.css";

export type BillingAccessState = {
  accessLevel: "active" | "trialing" | "grace_period" | "suspended" | "cancelled" | "no_subscription";
  actionRequired: boolean;
  applies: boolean;
  blockedCapabilities: string[];
  canManageBilling: boolean;
  failedSince?: string | null;
  failureReason?: string | null;
  graceEndsAt?: string | null;
  outstandingAmount: number;
  planType?: string | null;
  provider: "stripe" | "deluxe";
  providerStatus?: string;
  providerVerification?: "verified" | "unavailable" | "not_configured";
  providerVerifiedAt?: string | null;
  nextPaymentAt?: string | null;
  recoveryAvailable?: boolean;
  statusReason?: "provider_subscription_missing" | "provider_payment_required" | "subscription_payment_failed" | null;
  stripeTestMode?: boolean;
  subscriptionStatus?: string;
};

type SavedCard = {
  id: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  is_primary?: number;
};

type Props = {
  state: BillingAccessState;
  onDismiss: () => void;
  onRecovered: (state: BillingAccessState) => void;
};

type PaymentFormProps = {
  access: BillingAccessState;
  onRecovered: Props["onRecovered"];
};

declare const EmbeddedPayments: any;

function currency(value: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(value || 0));
}

function shortDate(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function SavedCardChoice({ cards, selected, onSelect }: { cards: SavedCard[]; selected: string; onSelect: (id: string) => void }) {
  if (!cards.length) return <Alert variant="light">No saved card is available. Add a new card to continue.</Alert>;
  return (
    <div className="billing-recovery__cards" role="radiogroup" aria-label="Saved payment methods">
      {cards.map((card) => (
        <button
          type="button"
          role="radio"
          aria-checked={selected === card.id}
          className={`billing-recovery__card${selected === card.id ? " is-selected" : ""}`}
          key={card.id}
          onClick={() => onSelect(card.id)}
        >
          <CreditCard size={20} aria-hidden="true" />
          <span><strong>{card.brand || "Card"} ending in {card.last4 || "••••"}</strong><small>{card.exp_month && card.exp_year ? `Expires ${card.exp_month}/${card.exp_year}` : "Saved payment method"}</small></span>
          {selected === card.id ? <Check size={18} aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );
}

function StripeNewCard({ disabled, submit }: { disabled: boolean; submit: (paymentMethodId: string) => Promise<void> }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    const card = elements?.getElement(CardElement);
    if (!stripe || !card) return;
    const result = await stripe.createPaymentMethod({ type: "card", card });
    if (result.error || !result.paymentMethod) {
      setError(result.error?.message || "Card could not be added.");
      return;
    }
    await submit(result.paymentMethod.id);
  };

  return (
    <div>
      <Form.Label>New card details</Form.Label>
      <div className="billing-recovery__stripe-field"><CardElement options={{ hidePostalCode: false }} /></div>
      {error ? <Alert variant="danger" className="mt-3 mb-0">{error}</Alert> : null}
      <Button className="w-100 mt-3" disabled={disabled || !stripe} onClick={() => void handleSubmit()}>
        {disabled ? <Spinner size="sm" animation="border" role="status" aria-label="Processing payment" /> : <CreditCard size={17} />} Pay securely
      </Button>
    </div>
  );
}

function RecoveryPaymentForm({ access, onRecovered }: PaymentFormProps) {
  const [cards, setCards] = useState<SavedCard[]>([]);
  const [selectedCard, setSelectedCard] = useState("");
  const [mode, setMode] = useState<"saved" | "new">("saved");
  const [loadingCards, setLoadingCards] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [deluxeReady, setDeluxeReady] = useState(false);
  const deluxeInitialized = useRef(false);

  useEffect(() => {
    let active = true;
    setLoadingCards(true);
    api.get("/stripe-sub/billing-recovery-cards")
      .then((response: any) => {
        if (!active) return;
        const nextCards = Array.isArray(response?.data) ? response.data : [];
        setCards(nextCards);
        setSelectedCard(nextCards[0]?.id || "");
        if (!nextCards.length) setMode("new");
      })
      .catch(() => active && setError("Saved payment methods could not be loaded."))
      .finally(() => active && setLoadingCards(false));
    return () => { active = false; };
  }, [access.provider]);

  const recover = useCallback(async (payload: Record<string, unknown>) => {
    setSubmitting(true);
    setError("");
    try {
      const response: any = await api.post("/stripe-sub/recover-subscription", {
        provider: access.provider,
        consent: true,
        consentSource: "billing-recovery-modal",
        ...payload,
      });
      onRecovered(response.data as BillingAccessState);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || requestError?.message || "Payment could not be completed.");
    } finally {
      setSubmitting(false);
    }
  }, [access.provider, onRecovered]);

  useEffect(() => {
    if (access.provider !== "deluxe" || mode !== "new") {
      deluxeInitialized.current = false;
      setDeluxeReady(false);
      return;
    }
    if (deluxeInitialized.current) return;
    let cancelled = false;
    api.post("/v2/api/deluxe/create-jwt", {})
      .then((response: any) => {
        if (cancelled) return;
        if (!response?.jwt || typeof EmbeddedPayments === "undefined") throw new Error("Secure card form is unavailable.");
        deluxeInitialized.current = true;
        setDeluxeReady(true);
        return EmbeddedPayments.init(response.jwt, {
          countryCode: "US",
          currencyCode: "USD",
          paymentMethods: ["cc"],
          collectBillingAddress: false,
          collectShippingAddress: false,
          skipSellerStatusCheck: true,
          skipCompleteAuthorization: true,
        }).then((instance: any) => instance.setEventHandlers({
          onTokenSuccess: (_gateway: string, data: any) => {
            const token = data?.Token || data?.token;
            const expiry = data?.ExpMonthYear || data?.expiry;
            if (!token || !expiry) {
              setError("Card details were not received. Please try again.");
              return;
            }
            void recover({ token, expiry });
          },
          onTokenFailed: () => setError("Card could not be secured. Please try again."),
          onValidationError: () => setError("Please check the card details and try again."),
          onTxnSuccess: () => undefined,
          onTxnFailed: () => undefined,
          onTxnAuthorized: () => undefined,
          onCancel: () => undefined,
        }).render({
          containerId: "billing-recovery-deluxe-card",
          paymentpanelstyle: document.documentElement.getAttribute("data-bs-theme") === "dark" ? "dark" : "light",
          paybuttoncolor: "#2563eb",
          cancelbuttoncolor: "#64748b",
        }));
      })
      .catch((initializationError: any) => setError(initializationError?.message || "Secure card form could not be loaded."));
    return () => { cancelled = true; };
  }, [access.provider, mode, recover]);

  if (loadingCards) return <div className="billing-recovery__loading" role="status"><Spinner animation="border" size="sm" /><span>Loading payment methods…</span></div>;

  return (
    <div>
      <div className="billing-recovery__tabs" role="tablist" aria-label="Payment method choice">
        <button type="button" role="tab" aria-selected={mode === "saved"} className={mode === "saved" ? "is-active" : ""} disabled={!cards.length} onClick={() => setMode("saved")}><CreditCard size={16} /> Saved card</button>
        <button type="button" role="tab" aria-selected={mode === "new"} className={mode === "new" ? "is-active" : ""} onClick={() => setMode("new")}><Plus size={16} /> New card</button>
      </div>

      {error ? <Alert variant="danger" className="mt-3">{error}</Alert> : null}
      {mode === "saved" ? (
        <>
          <SavedCardChoice cards={cards} selected={selectedCard} onSelect={setSelectedCard} />
          <Button className="w-100 mt-3" disabled={submitting || !selectedCard} onClick={() => void recover({ paymentMethodId: selectedCard })}>
            {submitting ? <Spinner size="sm" animation="border" role="status" aria-label="Processing payment" /> : <ShieldCheck size={17} />} Retry {currency(access.outstandingAmount)} securely
          </Button>
        </>
      ) : access.provider === "stripe" ? (
        <StripeNewCard disabled={submitting} submit={(paymentMethodId) => recover({ paymentMethodId })} />
      ) : (
        <div className="billing-recovery__new-card">
          {!deluxeReady ? <div className="billing-recovery__loading" role="status"><Loader2 className="billing-recovery__spin" size={18} /><span>Preparing secure card form…</span></div> : null}
          <div id="billing-recovery-deluxe-card" />
          {submitting ? <div className="billing-recovery__processing" role="status"><Spinner animation="border" size="sm" /><span>Processing your payment…</span></div> : null}
        </div>
      )}
    </div>
  );
}

export default function BillingRecoveryModal({ state, onDismiss, onRecovered }: Props) {
  const providerSubscriptionMissing = state.statusReason === "provider_subscription_missing";
  const title = providerSubscriptionMissing
    ? "Subscription is not active"
    : state.accessLevel === "suspended"
    ? "Subscription payment required"
    : "Your subscription payment failed";
  const context = useMemo(() => {
    if (state.statusReason === "provider_subscription_missing") {
      return "Your payment provider does not have an active subscription for this account. Choose a plan to restore premium actions.";
    }
    if (state.accessLevel === "suspended") return "New premium actions are temporarily unavailable. Your existing records and billing tools remain accessible.";
    const graceEnd = shortDate(state.graceEndsAt);
    return graceEnd ? `Your service remains available until ${graceEnd}. Please update payment to avoid interruption.` : "Please update your payment method to avoid service interruption.";
  }, [state.accessLevel, state.graceEndsAt, state.statusReason]);

  return (
    <Modal show onHide={onDismiss} centered size="lg" className="billing-recovery" aria-labelledby="billing-recovery-title">
      <Modal.Header closeButton>
        <div className="billing-recovery__title-icon"><AlertCircle size={22} aria-hidden="true" /></div>
        <div><Modal.Title id="billing-recovery-title">{title}</Modal.Title><p>{context}</p></div>
      </Modal.Header>
      <Modal.Body>
        <div className="billing-recovery__summary">
          {state.outstandingAmount > 0 ? <div><span>Amount due</span><strong>{currency(state.outstandingAmount)}</strong></div> : null}
          <div><span>Provider</span><strong>{state.provider === "deluxe" ? "Deluxe" : "Stripe"}</strong></div>
          {providerSubscriptionMissing ? <div><span>Provider status</span><strong>Inactive</strong></div> : null}
          {state.failedSince ? <div><span>Failed on</span><strong>{shortDate(state.failedSince)}</strong></div> : null}
        </div>
        {state.failureReason ? <Alert variant="warning"><strong>Payment note:</strong> {state.failureReason}</Alert> : null}
        {state.canManageBilling && state.recoveryAvailable !== false ? (
          <RecoveryPaymentForm access={state} onRecovered={onRecovered} />
        ) : state.canManageBilling && providerSubscriptionMissing ? (
          <Alert variant="info" className="mb-0">Select a subscription plan to create a new recurring subscription. No card will be charged from this dialog.</Alert>
        ) : (
          <Alert variant="info" className="mb-0">Only the company owner or a billing manager can update payment. Please contact them to restore service.</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button as={Link as any} to="/invoices-list" variant="outline-secondary" onClick={onDismiss}>View invoices</Button>
        <Button as={Link as any} to={providerSubscriptionMissing ? "/pages-profile-settings?tag=packages#packages" : "/pages-profile-settings#financial"} variant="outline-primary" onClick={onDismiss}>{providerSubscriptionMissing ? "Choose a plan" : "Payment settings"}</Button>
        <Button variant="link" onClick={onDismiss}>Remind me later</Button>
      </Modal.Footer>
    </Modal>
  );
}

export function BillingRecoveryModalWithStripe(props: Props) {
  return (
    <Elements stripe={props.state.stripeTestMode ? stripePromiseTest : stripePromise}>
      <BillingRecoveryModal {...props} />
    </Elements>
  );
}
